import React, { useEffect, useMemo, useState } from "react";
import { Post } from "@/core/types/postTypes";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { SpinnerIcon, WarningIcon } from "@/components/icons";
import { PostMediaPreview } from "./PostMediaPreview";
import {
  fetchTikTokCreatorInfo,
  TikTokCreatorInfo,
  TikTokPostOptions,
  TikTokPrivacyLevel,
  TIKTOK_PRIVACY_LABELS,
} from "./services/tiktokCreatorInfo";

// TikTok's documented caption ceiling for a direct post.
const TITLE_MAX = 2200;

const MUSIC_USAGE_URL =
  "https://www.tiktok.com/legal/page/global/music-usage-confirmation/en";
const BRANDED_CONTENT_URL = "https://www.tiktok.com/legal/page/global/bc-policy/en";

interface TikTokPostConsentModalProps {
  post: Post;
  clientId: string;
  onCancel: () => void;
  onConfirm: (options: TikTokPostOptions) => void;
}

/**
 * Pre-post consent screen required by TikTok's Content Sharing Guidelines.
 *
 * Every element here is mandated by the Content Posting API audit, so treat
 * the rules below as spec, not preference:
 *
 *  - the creator's nickname is shown so the user knows which account posts
 *  - the privacy selector has NO default value and lists exactly the levels
 *    `creator_info` returned
 *  - comment / duet / stitch start unchecked and are disabled (greyed) when
 *    the creator has turned them off in their own TikTok settings
 *  - commercial content disclosure defaults to off; when on, at least one of
 *    "Your brand" / "Branded content" must be picked
 *  - branded content may never be posted privately
 *  - the compliance declaration names the Music Usage Confirmation, plus the
 *    Branded Content Policy whenever branded content is selected
 *
 * Changing any of this can fail the audit. See docs/tiktok-audit/.
 */
export const TikTokPostConsentModal: React.FC<TikTokPostConsentModalProps> = ({
  post,
  clientId,
  onCancel,
  onConfirm,
}) => {
  const [creator, setCreator] = useState<TikTokCreatorInfo | null>(null);
  const [directPostEnabled, setDirectPostEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Editable title. TikTok requires that preset text be editable before
  // posting, so the post's caption is only the starting value.
  const [title, setTitle] = useState(post.caption || "");

  // No default privacy level: TikTok requires a deliberate choice.
  const [privacyLevel, setPrivacyLevel] = useState<TikTokPrivacyLevel | "">("");

  // Interaction controls start unchecked, per the guidelines.
  const [allowComment, setAllowComment] = useState(false);
  const [allowDuet, setAllowDuet] = useState(false);
  const [allowStitch, setAllowStitch] = useState(false);

  // Commercial content disclosure starts off.
  const [discloseCommercial, setDiscloseCommercial] = useState(false);
  const [yourBrand, setYourBrand] = useState(false);
  const [brandedContent, setBrandedContent] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const info = await fetchTikTokCreatorInfo(clientId);
        if (!cancelled) {
          setCreator(info.creator);
          setDirectPostEnabled(info.directPostEnabled);
        }
      } catch (error: any) {
        if (!cancelled) setLoadError(error.message || "Failed to load TikTok account");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // Branded content can never be private. If the creator picks branded
  // content while "Only you" is selected, drop the selection so they must
  // choose again rather than silently posting at a different visibility.
  useEffect(() => {
    if (brandedContent && privacyLevel === "SELF_ONLY") {
      setPrivacyLevel("");
    }
  }, [brandedContent, privacyLevel]);

  // Turning the disclosure off clears both labels.
  useEffect(() => {
    if (!discloseCommercial) {
      setYourBrand(false);
      setBrandedContent(false);
    }
  }, [discloseCommercial]);

  const privacyOptions = creator?.privacyLevelOptions ?? [];

  // The guidelines require checking the video against the creator's own
  // max_video_post_duration_sec before allowing a post, rather than letting
  // TikTok reject it after the upload has already happened.
  const videoDuration = useMemo(() => {
    const video = (post.mediaItems || []).find(
      (m: any) => m?.mediaType === "video" || m?.duration || m?.durationSeconds
    ) as any;
    return video ? (video.duration ?? video.durationSeconds ?? null) : null;
  }, [post.mediaItems]);

  const tooLong =
    !!creator?.maxVideoPostDurationSec &&
    !!videoDuration &&
    videoDuration > creator.maxVideoPostDurationSec;

  const commercialLabel = useMemo(() => {
    if (brandedContent) {
      return "Your video will be labeled as “Paid partnership”";
    }
    if (yourBrand) {
      return "Your video will be labeled as “Promotional content”";
    }
    return null;
  }, [yourBrand, brandedContent]);

  const disclosureIncomplete = discloseCommercial && !yourBrand && !brandedContent;

  const canPost =
    !!creator &&
    privacyLevel !== "" &&
    !disclosureIncomplete &&
    !tooLong &&
    title.trim().length > 0;

  const handleConfirm = () => {
    // Test the union member FIRST. Written as `!canPost || privacyLevel === ""`
    // TypeScript narrows privacyLevel through the aliased `canPost` condition
    // (which already contains `privacyLevel !== ""`), decides the remaining
    // type cannot be "", and rejects the comparison as unintentional (TS2367).
    // Checking it up front keeps the guard and the narrowing honest.
    if (privacyLevel === "" || !canPost) return;
    onConfirm({
      title,
      privacyLevel,
      disableComment: !allowComment,
      disableDuet: !allowDuet,
      disableStitch: !allowStitch,
      brandOrganicToggle: yourBrand,
      brandContentToggle: brandedContent,
    });
  };

  const renderInteractionToggle = (
    label: string,
    checked: boolean,
    setChecked: (value: boolean) => void,
    disabledByCreator: boolean
  ) => (
    <label
      className={`flex items-center gap-2 text-sm ${
        disabledByCreator ? "text-gray-400 cursor-not-allowed" : "text-gray-700 cursor-pointer"
      }`}
      title={
        disabledByCreator
          ? "Turned off in this creator's TikTok settings"
          : undefined
      }
    >
      <input
        type="checkbox"
        className="rounded border-gray-300 disabled:opacity-50"
        checked={checked && !disabledByCreator}
        disabled={disabledByCreator}
        onChange={(e) => setChecked(e.target.checked)}
      />
      {label}
    </label>
  );

  return (
    <Modal
      isOpen={true}
      onClose={onCancel}
      title="Post to TikTok"
      size="lg"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleConfirm}
            disabled={!canPost}
          >
            {directPostEnabled ? "Post to TikTok" : "Send to TikTok drafts"}
          </Button>
        </div>
      }
    >
      <div className="p-6">
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-600 py-8 justify-center">
            <SpinnerIcon className="w-4 h-4" />
            Checking TikTok posting options...
          </div>
        )}

        {loadError && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <WarningIcon className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-800">
                TikTok is not accepting posts right now
              </p>
              <p className="text-sm text-red-700 mt-1">{loadError}</p>
            </div>
          </div>
        )}

        {creator && !isLoading && (
          <div className="space-y-6">
            {/* Account + preview */}
            <div className="flex items-start gap-4">
              <div className="w-32 shrink-0">
                <PostMediaPreview post={post} clientId={clientId} />
              </div>
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wide text-gray-500">
                  Posting to
                </p>
                <div className="flex items-center gap-2 mt-1">
                  {creator.avatarUrl && (
                    <img
                      src={creator.avatarUrl}
                      alt=""
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  )}
                  <div>
                    <p className="font-medium text-sm">
                      {creator.nickname || creator.username || "TikTok account"}
                    </p>
                    {creator.username && (
                      <p className="text-xs text-gray-500">@{creator.username}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Editable title - required: preset text must be editable */}
            <div>
              <label htmlFor="tiktok-title" className="block text-sm font-medium mb-1">
                Caption
              </label>
              <textarea
                id="tiktok-title"
                className="w-full border rounded-lg px-3 py-2 text-sm"
                rows={3}
                maxLength={TITLE_MAX}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Write a caption for this TikTok post"
              />
              <p className="text-xs text-gray-400 mt-1">
                {title.length} / {TITLE_MAX}
              </p>
            </div>

            {/* Duration check against the creator's allowance */}
            {tooLong && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                This video is {Math.round(videoDuration ?? 0)}s, longer than the{" "}
                {creator.maxVideoPostDurationSec}s this account can post. TikTok
                will reject it - trim the video before posting.
              </div>
            )}

            {/* Privacy level — no default value */}
            <div>
              <label
                htmlFor="tiktok-privacy"
                className="block text-sm font-medium mb-1"
              >
                Who can view this video
              </label>
              <select
                id="tiktok-privacy"
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={privacyLevel}
                onChange={(e) =>
                  setPrivacyLevel(e.target.value as TikTokPrivacyLevel)
                }
              >
                <option value="" disabled>
                  Select an option
                </option>
                {privacyOptions.map((level) => {
                  const blockedByBranded =
                    brandedContent && level === "SELF_ONLY";
                  return (
                    <option
                      key={level}
                      value={level}
                      disabled={blockedByBranded}
                      title={
                        blockedByBranded
                          ? "Branded content visibility cannot be set to private"
                          : undefined
                      }
                    >
                      {TIKTOK_PRIVACY_LABELS[level] || level}
                    </option>
                  );
                })}
              </select>
              {brandedContent && (
                <p className="text-xs text-amber-700 mt-1">
                  Branded content visibility cannot be set to private
                </p>
              )}
              {privacyOptions.length === 1 &&
                privacyOptions[0] === "SELF_ONLY" && (
                  <p className="text-xs text-gray-500 mt-1">
                    TikTok is only allowing private posts for this account.
                    This is what an app sees before its Content Posting API
                    audit is approved.
                  </p>
                )}
            </div>

            {/* Interaction controls */}
            <div>
              <p className="text-sm font-medium mb-2">Allow users to</p>
              <div className="flex flex-col gap-2">
                {renderInteractionToggle(
                  "Comment",
                  allowComment,
                  setAllowComment,
                  creator.commentDisabled
                )}
                {renderInteractionToggle(
                  "Duet",
                  allowDuet,
                  setAllowDuet,
                  creator.duetDisabled
                )}
                {renderInteractionToggle(
                  "Stitch",
                  allowStitch,
                  setAllowStitch,
                  creator.stitchDisabled
                )}
              </div>
            </div>

            {/* Commercial content disclosure */}
            <div className="border-t pt-4">
              <label className="flex items-start justify-between gap-4 cursor-pointer">
                <span>
                  <span className="block text-sm font-medium">
                    Disclose video content
                  </span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Turn on to declare that this video promotes a brand,
                    product or service.
                  </span>
                </span>
                <input
                  type="checkbox"
                  className="mt-1 rounded border-gray-300"
                  checked={discloseCommercial}
                  onChange={(e) => setDiscloseCommercial(e.target.checked)}
                />
              </label>

              {discloseCommercial && (
                <div className="mt-3 pl-1 flex flex-col gap-2">
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1 rounded border-gray-300"
                      checked={yourBrand}
                      onChange={(e) => setYourBrand(e.target.checked)}
                    />
                    <span>
                      <span className="block">Your brand</span>
                      <span className="block text-xs text-gray-500">
                        You are promoting yourself or your own business.
                      </span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1 rounded border-gray-300"
                      checked={brandedContent}
                      onChange={(e) => setBrandedContent(e.target.checked)}
                    />
                    <span>
                      <span className="block">Branded content</span>
                      <span className="block text-xs text-gray-500">
                        You are promoting another brand or a third party.
                      </span>
                    </span>
                  </label>

                  {commercialLabel && (
                    <p className="text-xs text-gray-700 bg-gray-50 border rounded px-3 py-2">
                      {commercialLabel}
                    </p>
                  )}
                  {disclosureIncomplete && (
                    <p className="text-xs text-amber-700">
                      You need to indicate if your content promotes yourself,
                      a third party, or both.
                    </p>
                  )}
                </div>
              )}
            </div>

            {!directPostEnabled && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                Direct posting is off for this deployment, so this video will
                go to the account&rsquo;s TikTok drafts and the visibility
                chosen here will not be applied — the creator picks it when
                they publish. You normally shouldn&rsquo;t see this screen in
                draft mode.
              </div>
            )}

            <p className="text-xs text-gray-500">
              After posting, TikTok may take a few minutes to process the video
              before it is visible on the account.
            </p>

            {/* Compliance declaration */}
            <p className="text-xs text-gray-500 border-t pt-4">
              By posting, you agree to TikTok&rsquo;s{" "}
              {brandedContent && (
                <>
                  <a
                    href={BRANDED_CONTENT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    Branded Content Policy
                  </a>{" "}
                  and{" "}
                </>
              )}
              <a
                href={MUSIC_USAGE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Music Usage Confirmation
              </a>
              .
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};
